import { Provider, useSelector } from 'react-redux';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { store } from '@/store/store';
import { selectIsAuthenticated, selectIsAuthLoading } from '@/store/authSlice';
import theme from '@/styles/theme';
import ErrorBoundary from '@/components/common/ErrorBoundary';
import AuthManager from '@/components/AuthManager';
import AppLayout from '@/components/layout/AppLayout';
import SearchPage from '@/pages/SearchPage';
import DocumentsPage from '@/pages/DocumentsPage';
import UploadPage from '@/pages/UploadPage';
import AdminPage from '@/pages/AdminPage';
import PortalCredentialsPage from '@/pages/PortalCredentialsPage';
import DocumentDetailPage from '@/pages/DocumentDetailPage';
import CustomersPage from '@/pages/CustomersPage';
import CustomerDetailPage from '@/pages/CustomerDetailPage';
import SprawyPage from '@/pages/SprawyPage';
import SprawaDetailPage from '@/pages/SprawaDetailPage';
import SprawyBoardPage from '@/pages/SprawyBoardPage';
import SprawyCalendarPage from '@/pages/SprawyCalendarPage';
import PelnomocnicyPage from '@/pages/PelnomocnicyPage';
import CourtsPage from '@/pages/CourtsPage';
import CourtDetailPage from '@/pages/CourtDetailPage';
import PelnomocnikDetailPage from '@/pages/PelnomocnikDetailPage';
import WelcomePage from '@/pages/WelcomePage';
import LoginPage from '@/pages/LoginPage';
import SignupPage from '@/pages/SignupPage';
import SsoCallbackPage from '@/pages/SsoCallbackPage';
import AuthErrorPage from '@/pages/AuthErrorPage';
import MagicLinkPage from '@/pages/MagicLinkPage';
import AcceptInvitationPage from '@/pages/AcceptInvitationPage';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { ToastProvider } from '@/components/common/ToastProvider';
import PublicUploadPage from '@/pages/PublicUploadPage';
import LegalProfessionalDirectoryPage from '@/pages/LegalProfessionalDirectoryPage';
import LegalProfessionalDetailPage from '@/pages/LegalProfessionalDetailPage';
import NotFoundPage from '@/pages/states/NotFoundPage';
import SettingsPage from '@/pages/SettingsPage';

function ProtectedRoute() {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isLoading = useSelector(selectIsAuthLoading);

  if (isLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}

function PublicRoute() {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isLoading = useSelector(selectIsAuthLoading);

  if (isLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/sprawy" replace />;
  }

  return <Outlet />;
}

function AppRoutes() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/upload/:token" element={<PublicUploadPage />} />
        <Route element={<PublicRoute />}>
          <Route path="/" element={<WelcomePage />} />
        </Route>
        <Route element={<ProtectedRoute />}>
          <Route element={<AppLayout />}>
            <Route path="/documents" element={<DocumentsPage />} />
            <Route path="/documents/search" element={<SearchPage />} />
            <Route path="/documents/upload" element={<UploadPage />} />
            <Route path="/documents/:id" element={<DocumentDetailPage />} />
            <Route path="/customers" element={<CustomersPage />} />
            <Route path="/customers/:id" element={<CustomerDetailPage />} />
            <Route path="/sprawy" element={<SprawyPage />} />
            <Route path="/sprawy/board" element={<SprawyBoardPage />} />
            <Route path="/sprawy/calendar" element={<SprawyCalendarPage />} />
            <Route path="/sprawy/:id" element={<SprawaDetailPage />} />
            <Route path="/courts" element={<CourtsPage />} />
            <Route path="/courts/:id" element={<CourtDetailPage />} />
            <Route path="/pelnomocnicy" element={<PelnomocnicyPage />} />
            <Route path="/pelnomocnicy/:id" element={<PelnomocnikDetailPage />} />
            <Route path="/legal-professionals" element={<LegalProfessionalDirectoryPage />} />
            <Route path="/legal-professionals/:id" element={<LegalProfessionalDetailPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/admin/portal-credentials" element={<PortalCredentialsPage />} />
            {/* Redirects for old routes */}
            <Route path="/search" element={<Navigate to="/documents/search" replace />} />
            <Route path="/upload" element={<Navigate to="/documents/upload" replace />} />
          </Route>
        </Route>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/sso/callback" element={<SsoCallbackPage />} />
        <Route path="/auth/error" element={<AuthErrorPage />} />
        <Route path="/auth/magic" element={<MagicLinkPage />} />
        <Route path="/invite" element={<AcceptInvitationPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <ErrorBoundary>
          <ToastProvider>
            <AuthManager>
              <AppRoutes />
            </AuthManager>
          </ToastProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </Provider>
  );
}
